import React, { useState, useRef } from "react";
import { Box, Typography, Button, Grid, CircularProgress } from "@mui/material";
import EditTextModal from "../EditTextModal/EditTextModal";
import TemplateImage from "../../assets/templates/template-14.png";
import TextPlacehoder from "../TextPlaceholder/TextPlaceholder";
import ImagePlaceholder from "../ImagePlaceholder/ImagePlaceholder";
import imageCompression from "browser-image-compression";
import { useSnackbar } from "notistack";
import { useMediaQuery } from "@uidotdev/usehooks";

const Template14 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
}) => {
	const isSmallScreen = useMediaQuery("(max-width: 600px)");
	const [loadingImage, setLoadingImage] = useState([
		false,
		false,
		false,
		false,
	]);

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [modalText, setModalText] = useState("");
	const [selectedTextIndex, setSelectedTextIndex] = useState(null);

	const inputRefs = [useRef(null), useRef(null), useRef(null)];
	const cancelHandlerRefs = [useRef(null), useRef(null), useRef(null)];
	const processingRefs = [useRef(false), useRef(false), useRef(false)];

	const { enqueueSnackbar } = useSnackbar();
	const compressImage = async (file) => {
		const options = {
			maxSizeMB: 0.75, // (Max file size in MB)
			maxWidthOrHeight: 1280, // (Compressed file's maximum dimension in pixels)
			useWebWorker: true, // (Use a web worker to perform the compression in a background thread),
			maxIterations: 100,
		};

		try {
			const compressedFile = await imageCompression(file, options);
			return compressedFile; // This is the compressed image file
		} catch (error) {
			enqueueSnackbar(
				"La imagen no es compatible. Inténtalo con una distinta.",
				{ variant: "error" },
			);
			console.error(error);
			return null;
		}
	};

	const handleImageChange = async (event, index) => {
		// Marcar que estamos procesando un archivo
		processingRefs[index].current = true;

		// Limpiar el handler de cancelación si existe (usuario seleccionó un archivo)
		if (cancelHandlerRefs[index].current) {
			window.removeEventListener("focus", cancelHandlerRefs[index].current);
			cancelHandlerRefs[index].current = null;
		}

		const rawFile = event.target.files[0];
		if (!rawFile) {
			processingRefs[index].current = false;
			setLoadingImage((prev) => {
				let auxPrev = [...prev];
				auxPrev[index] = false;
				return auxPrev;
			});
			return;
		}

		// El loading ya está activo desde handleClickImageInput
		// Mantenerlo activo durante la compresión
		const file = await compressImage(rawFile);
		if (file) {
			try {
				const reader = new FileReader();
				reader.onload = () => {
					let auxPage = { ...page };
					auxPage.images[index] = {
						id: -new Date().getTime(),
						src: reader.result,
					};
					updatePage({ ...auxPage });
					processingRefs[index].current = false;
					setLoadingImage((prev) => {
						let auxPrev = [...prev];
						auxPrev[index] = false;
						return auxPrev;
					});
				};
				reader.onerror = () => {
					processingRefs[index].current = false;
					setLoadingImage((prev) => {
						let auxPrev = [...prev];
						auxPrev[index] = false;
						return auxPrev;
					});
				};
				reader.readAsDataURL(file);
			} catch (error) {
				console.error("Error al cargar la imagen:", error);
				processingRefs[index].current = false;
				setLoadingImage((prev) => {
					let auxPrev = [...prev];
					auxPrev[index] = false;
					return auxPrev;
				});
			}
		} else {
			processingRefs[index].current = false;
			setLoadingImage((prev) => {
				let auxPrev = [...prev];
				auxPrev[index] = false;
				return auxPrev;
			});
		}
	};

	const handleClickImageInput = (index) => {
		const inputElement = inputRefs[index].current;
		if (inputElement) {
			// Limpiar handler anterior si existe
			if (cancelHandlerRefs[index].current) {
				window.removeEventListener("focus", cancelHandlerRefs[index].current);
			}

			// Activar loading al hacer clic
			setLoadingImage((prev) => {
				let auxPrev = [...prev];
				auxPrev[index] = true;
				return auxPrev;
			});

			// Crear handler para detectar cuando el usuario vuelve a la ventana
			// Si no hay archivo seleccionado cuando vuelve, significa que canceló
			cancelHandlerRefs[index].current = () => {
				// Delay para asegurar que el onChange se haya procesado si seleccionó un archivo
				setTimeout(() => {
					// Si ya estamos procesando un archivo, no hacer nada
					if (processingRefs[index].current) {
						// Limpiar el handler pero no tocar el loading
						if (cancelHandlerRefs[index].current) {
							window.removeEventListener(
								"focus",
								cancelHandlerRefs[index].current,
							);
							cancelHandlerRefs[index].current = null;
						}
						return;
					}

					const hasFile = inputElement.files && inputElement.files.length > 0;
					if (!hasFile) {
						// No hay archivo y no estamos procesando, el usuario canceló - desactivar loading
						setLoadingImage((prev) => {
							if (prev[index]) {
								// Solo desactivar si aún está activo
								let auxPrev = [...prev];
								auxPrev[index] = false;
								return auxPrev;
							}
							return prev;
						});
					}
					// Limpiar el handler
					if (cancelHandlerRefs[index].current) {
						window.removeEventListener(
							"focus",
							cancelHandlerRefs[index].current,
						);
						cancelHandlerRefs[index].current = null;
					}
				}, 300); // Delay de 300ms para dar tiempo al onChange
			};

			// Agregar listener de focus al window (solo una vez)
			window.addEventListener("focus", cancelHandlerRefs[index].current, {
				once: true,
			});

			inputElement.click();
		}
	};

	const handleTextClick = (index) => {
		setSelectedTextIndex(index);
		setModalText(page.texts?.[index]?.content ?? "");
		setIsModalOpen(true);
	};

	const handleModalTextChange = (value) => {
		setModalText(value);
	};

	const handleModalSave = () => {
		let auxPage = { ...page };
		auxPage.texts[selectedTextIndex] = {
			content: modalText,
			isEditing: false,
		};
		updatePage({ ...auxPage });

		setIsModalOpen(false);
	};

	const handleModalCancel = () => {
		setIsModalOpen(false);
	};

	if (!page?.texts) {
		return null;
	}

	return (
		<Box
			sx={{
				backgroundColor: backgroundColor,
				display: "flex",
				minHeight: isSmallScreen ? "auto" : "100%",
				height: "auto",
				flexDirection: "column",
				"& *": {
					fontFamily: fontFamily + "!important",
				},
			}}
		>
			<Box
				data-template-item="true"
				data-block-id="header"
				onClick={() => handleTextClick(0)}
				style={{
					height: "51px",
					minHeight: "51px",
					width: "100%",
					backgroundColor: headerBackgroundColor,
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					textAlign: "center",
					position: "sticky",
					top: 0,
					zIndex: 1053,
				}}
			>
				<Typography
					data-template-item="true"
					onClick={() => handleTextClick(0)}
					sx={{
						display: "flex!important",
						justifyContent: "center",
						height: "100%",
						alignItems: "center",
						color: headerTextColor,
						cursor: "pointer",
						textAlign: "center",
						fontSize: "14px!important",
						fontWeight: "bold!important",
					}}
					dangerouslySetInnerHTML={{
						__html: page.texts?.[0]?.content ?? "Título de la página",
					}}
				/>
			</Box>
			<Typography
				data-template-item="true"
				data-block-id="text-1"
				onClick={() => handleTextClick(1)}
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					color: textColor,
					cursor: "pointer",
					padding: "10px",
					maxHeight: "100%",
					marginTop: "18px",
					paddingTop: "5px",
					paddingBottom: "2px",
					paddingLeft: "15px!important",
				}}
				dangerouslySetInnerHTML={{
					__html:
						(page.texts?.[1]?.content ?? "") !== ""
							? (page.texts?.[1]?.content ?? "")
							: `<p style="font-weight: 700">Título</p>`,
				}}
			/>
			{(page.texts?.[2]?.content ?? "") !== "" ? (
				<Typography
					data-template-item="true"
					data-block-id="text-2"
					onClick={() => handleTextClick(2)}
					sx={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						color: textColor,
						cursor: "pointer",
						padding: "10px",
						paddingTop: "5px",
						paddingBottom: "2px",
						paddingLeft: "15px!important",
						maxHeight: "150px",
						overflow: "hidden",
						marginBottom: 2,
						marginTop: 2,
					}}
					dangerouslySetInnerHTML={{ __html: page.texts?.[2]?.content ?? "" }}
				/>
			) : (
				<Box sx={{ paddingLeft: 2, paddingTop: 2 }} data-block-id="text-2">
					<TextPlacehoder
						rows={2}
						callback={() => {
							handleTextClick(2);
						}}
					/>
				</Box>
			)}

			<input
				type="file"
				accept="image/*"
				class="template-item"
				onChange={(e) => handleImageChange(e, 0)}
				style={{ display: "none" }}
				ref={inputRefs[0]}
			/>
			<label
				style={{
					width: "100%",
					height: "215px",
					display: "block",
					position: "relative",
				}}
				htmlFor={`image1Input`}
				data-template-item={true}
				data-block-id="image-0"
				class="template-item"
				onClick={() => handleClickImageInput(0)}
			>
				{page.images?.[0]?.src ? (
					<div
						onClick={(e) => {
							e.stopPropagation();
							e.preventDefault();
							handleClickImageInput(0);
						}}
						onMouseDown={(e) => {
							e.stopPropagation();
						}}
						className="template-item"
						data-template-item
						style={{
							width: "100%",
							height: "100%",
							objectFit: "cover",
							backgroundImage: `url(${page.images[0].src})`,
							backgroundSize: "cover",
							backgroundPosition: "center",
							cursor: "pointer",
							zIndex: 1001,
							pointerEvents: "auto",
						}}
					></div>
				) : (
					!loadingImage[0] && (
						<ImagePlaceholder
							height="215px"
							onClick={(e) => {
								e.stopPropagation();
								e.preventDefault();
								handleClickImageInput(0);
							}}
						/>
					)
				)}
				{loadingImage[0] && (
					<Box
						sx={{
							position: "absolute",
							top: 0,
							left: 0,
							width: "100%",
							height: "100%",
							backgroundColor: "rgba(0, 0, 0, 0.15)",
							padding: 1,
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<CircularProgress size={24} />
					</Box>
				)}
			</label>
			<Typography
				data-template-item="true"
				data-block-id="text-3"
				onClick={() => handleTextClick(3)}
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					color: textColor,
					cursor: "pointer",
					padding: "10px",
					paddingTop: "5px",
					paddingBottom: "2px",
					paddingLeft: "15px!important",
					maxHeight: "150px",
					overflow: "hidden",
					marginTop: 2,
				}}
				dangerouslySetInnerHTML={{
					__html:
						(page.texts?.[3]?.content ?? "") !== ""
							? (page.texts?.[3]?.content ?? "")
							: `<p style="font-weight: 700">Título</p>`,
				}}
			/>

			{(page.texts?.[4]?.content ?? "") !== "" ? (
				<Typography
					data-template-item="true"
					data-block-id="text-4"
					onClick={() => handleTextClick(4)}
					sx={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						color: textColor,
						cursor: "pointer",
						padding: "10px",
						maxHeight: "100%",
						marginTop: "18px",
						paddingTop: "5px",
						paddingBottom: "2px",
						paddingLeft: "15px!important",
					}}
					dangerouslySetInnerHTML={{ __html: page.texts?.[4]?.content ?? "" }}
				/>
			) : (
				<Box sx={{ paddingLeft: 2, paddingTop: 2 }} data-block-id="text-4">
					<TextPlacehoder
						rows={2}
						callback={() => {
							handleTextClick(4);
						}}
					/>
				</Box>
			)}
			<Box sx={{ flex: 1, overflow: "hidden", height: "100%" }}>
				<Grid container padding={2} sx={{ height: "100%" }}>
					<Grid
						item
						xs={6}
						sx={{ maxHeight: "100%", backgroundColor: "#f1f2f6" }}
						data-block-id="image-1"
					>
						<input
							type="file"
							accept="image/*"
							class="template-item"
							onChange={(e) => handleImageChange(e, 1)}
							style={{ display: "none" }}
							ref={inputRefs[1]}
						/>
						<label
							style={{
								width: "100%",
								height: "100%",
								display: "block",
								position: "relative",
							}}
							htmlFor={`image1Input`}
							class="template-item"
							onClick={() => handleClickImageInput(1)}
						>
							{page.images?.[1]?.src ? (
								<div
									onClick={(e) => {
										e.stopPropagation();
										e.preventDefault();
										handleClickImageInput(1);
									}}
									onMouseDown={(e) => {
										e.stopPropagation();
									}}
									className="template-item"
									data-template-item
									style={{
										width: "100%",
										height: "100%",
										objectFit: "cover",
										backgroundImage: `url(${page.images[1].src})`,
										backgroundSize: "cover",
										backgroundPosition: "center",
										cursor: "pointer",
										zIndex: 1001,
										pointerEvents: "auto",
									}}
								></div>
							) : (
								!loadingImage[1] && (
									<ImagePlaceholder
										height="100%"
										onClick={(e) => {
											e.stopPropagation();
											e.preventDefault();
											handleClickImageInput(1);
										}}
									/>
								)
							)}
							{loadingImage[1] && (
								<Box
									sx={{
										position: "absolute",
										top: 0,
										left: 0,
										width: "100%",
										height: "100%",
										backgroundColor: "rgba(0, 0, 0, 0.15)",
										padding: 1,
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
									}}
								>
									<CircularProgress size={24} />
								</Box>
							)}
						</label>
					</Grid>
					<Grid item xs={6} sx={{ maxHeight: "100%" }} data-block-id="text-5">
						{(page.texts?.[5]?.content ?? "") !== "" ? (
							<Typography
								data-template-item="true"
								onClick={() => handleTextClick(5)}
								sx={{
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
									color: textColor,
									cursor: "pointer",
									padding: "10px",
									maxHeight: "100%",
									paddingTop: "5px",
									paddingBottom: "2px",
									paddingLeft: "15px!important",
								}}
								dangerouslySetInnerHTML={{
									__html: page.texts?.[5]?.content ?? "",
								}}
							/>
						) : (
							<TextPlacehoder
								callback={() => {
									handleTextClick(5);
								}}
							/>
						)}
					</Grid>
				</Grid>
			</Box>

			<EditTextModal
				showModal={isModalOpen}
				handleClose={handleModalCancel}
				value={modalText}
				onChange={handleModalTextChange}
				onSave={handleModalSave}
			/>
		</Box>
	);
};

Template14.thumbnail = TemplateImage;

export default Template14;
